import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

import { trackingEventStore } from '~/behavior-tracking/utility/tracking-events-store'
import useCurrentFamily from '~/familyAccount/hooks/useCurrentFamily'
import useCurrentUser from '~/hooks/useCurrentUser'
import { getCanonicalEmailForTracking, isImpactChild, pushImpactIdentify, sha1Hex } from '~/utils/analytics/impactUtils'
import appConfig from '~/config/app'

interface EmailCache {
  email: string
  sha1: string
}

/**
 * Pushes an impact_identify dataLayer event on every route change for both
 * anonymous and logged-in users. GTM consumes this to fire ire('identify',...).
 * Mirrors the useLogPageView pattern in src/utils/analytics/index.ts.
 */
export function useImpactIdentify(): void {
  const router = useRouter()
  const user = useCurrentUser()
  const { currentUserData: currentFamilyData, isFamilyLoading } = useCurrentFamily()
  const emailCacheRef = useRef<EmailCache | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (appConfig.isSchoolPlatform) return
    if (!isFamilyLoading && isImpactChild(currentFamilyData)) return

    const controller = new AbortController()

    void (async () => {
      await trackingEventStore.ready
      if (controller.signal.aborted) return

      const pseudoId = (await trackingEventStore.getPseudoId()) ?? ''
      if (controller.signal.aborted) return

      let hashedEmail = ''
      if (user) {
        const email = getCanonicalEmailForTracking(user)
        if (email) {
          // Memoize hash to avoid redundant SubtleCrypto calls on the same email.
          if (emailCacheRef.current?.email === email) {
            hashedEmail = emailCacheRef.current.sha1
          } else {
            hashedEmail = await sha1Hex(email, controller.signal)
            if (!controller.signal.aborted) {
              emailCacheRef.current = { email, sha1: hashedEmail }
            }
          }
        }
      }

      if (controller.signal.aborted) return

      pushImpactIdentify({
        customerId: user ? String(user.id) : '',
        customerEmail: hashedEmail,
        customProfileId: pseudoId,
      })
    })()

    return () => {
      controller.abort()
    }
    // Identity fields are listed individually because `user` is recreated on every Redux
    // dispatch — depending on the object reference would re-fire identify on every route
    // change, even for unrelated state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, user?.id, user?.email, user?.authEmail, user?.facebookEmail, currentFamilyData?.role])
}
